import React from 'react';
import { Video } from 'lucide-react';
import { describeMeetingState, isMeetingActive } from '../../lib/meetBot';

/**
 * Фаза «Бот на встрече».
 *
 * 🔴 Отдельная фаза, а не разновидность «обработки»: бот ждёт часами. Показать
 * здесь полосу прогресса значило бы обещать скорое завершение, которого не
 * будет, поэтому вместо процентов — шаги и время с начала.
 */

// 🔴 Заголовок называет стадию, а не фазу экрана. Раньше он всегда был «Бот на
// встрече» — и после конца встречи тоже, что путало (обращение ffebe7cb,
// 24.09.2026). Без состояния (до первого ответа сервиса) — прежнее название.
// Таймер — подпись к состоянию, а не действие: идёт, пока бот в комнате.
// Кнопки решает сервис (ниже), подписи — экран.
const IN_ROOM_STATES = ['JOINING', 'WAITING_ROOM', 'RECORDING'];

const TITLES = {
  JOINING: 'Бот подключается',
  WAITING_ROOM: 'Бот ждёт, когда впустят',
  RECORDING: 'Бот на встрече',
  PROCESSING: 'Встреча закончилась',
  DONE: 'Расшифровка готова',
  ERROR: 'Запись не удалась',
};

// Шаги привязаны к состояниям bit-meetbot: пока бот в комнате ожидания, вход
// ещё не случился, и отмечать его сделанным нельзя. Подпись сделанного шага —
// в прошедшем времени: «уйдёт сам» у кончившейся встречи читается как «ещё там».
const STEPS = [
  { id: 'join', label: 'Зайдёт в комнату', doneLabel: 'Зашёл в комнату',
    doneAfter: ['RECORDING', 'PROCESSING', 'DONE'] },
  { id: 'announce', label: 'Объявит о записи вслух', doneLabel: 'Объявил о записи вслух',
    doneAfter: ['RECORDING', 'PROCESSING', 'DONE'] },
  { id: 'record', label: 'Пишет звук и запоминает, кто говорит',
    doneLabel: 'Записал звук и имена говорящих', doneAfter: ['PROCESSING', 'DONE'] },
  { id: 'leave', label: 'Уйдёт сам и отдаст расшифровку', nowLabel: 'Вышел, готовит расшифровку',
    doneLabel: 'Расшифровка в Истории', doneAfter: ['DONE'] },
];

function stepLabel(step, status) {
  if (status === 'done') return step.doneLabel;
  if (status === 'now' && step.nowLabel) return step.nowLabel;
  return step.label;
}

function stepState(step, meetingState, index) {
  if (step.doneAfter.includes(meetingState)) return 'done';
  // У упавшей встречи текущего шага нет: пульсирующая точка обещала бы, что
  // бот ещё что-то делает.
  if (!isMeetingActive(meetingState)) return 'pending';
  // Текущий — первый невыполненный: у комнаты ожидания это вход, у записи —
  // сама запись.
  const firstPending = STEPS.findIndex((s) => !s.doneAfter.includes(meetingState));
  return index === firstPending ? 'now' : 'pending';
}

function formatElapsed(seconds) {
  if (typeof seconds !== 'number' || seconds < 0) return null;
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

const MeetingState = ({ state, error, elapsedSec, url, onStop, stopping, onNew,
                        canStop = false, canStartNew = false }) => {
  // 🔴 Кнопки решает сервис (`can_stop`, `can_start_new` в ответе bit-meetbot),
  // а не экран по своему списку состояний. До 05.10 экран считал живой и
  // встречу, у которой готовится расшифровка: после «Остановить запись»
  // кнопка оставалась на месте, таймер писал «подключается», а «Новая запись»
  // не появлялась, хотя бот новую встречу уже принимал.
  // 🔴 «Записывает» — только когда запись ИДЁТ. Раньше строка показывалась на
  // любом живом состоянии, то есть и пока бот стучится в комнату ожидания:
  // продукт утверждал, что пишет, когда не писал ничего (жалоба Владимира
  // 21.09.2026). Человек, положившийся на эту надпись, теряет встречу.
  const recording = state === 'RECORDING';
  const elapsed = formatElapsed(elapsedSec);

  return (
    <div role="region" aria-label="Бот на встрече"
      className="flex-1 min-h-[300px] bg-paper-card rounded-card px-5 py-7 md:px-9 md:py-9 flex flex-col items-center justify-center gap-4 text-center">
      <span className="w-[84px] h-[84px] rounded-full bg-paper-bg flex items-center justify-center text-paper-ink">
        <Video size={30} strokeWidth={1.5} />
      </span>

      <h2 className="m-0 font-display text-[28px] font-extrabold tracking-[-0.03em] text-paper-ink">
        {TITLES[state] || 'Бот на встрече'}
      </h2>

      {IN_ROOM_STATES.includes(state) && elapsed && (
        <div className={`flex items-center gap-2 font-mono text-[13px] ${
          recording ? 'text-paper-accent' : 'text-paper-ink-3'}`}>
          {/* Пульсирующая точка — признак записи, а не «что-то происходит»:
              до входа в комнату писать нечего. */}
          {recording && (
            <span className="w-[6px] h-[6px] rounded-full bg-paper-accent animate-pulse" />
          )}
          {recording ? `записывает · ${elapsed}` : `подключается · ${elapsed}`}
        </div>
      )}

      {/* Ссылка на встречу: если встреч несколько, без неё понять, за какой
          именно следит бот, нельзя. */}
      {url && (
        <a href={url} target="_blank" rel="noreferrer"
          className="max-w-full truncate font-mono text-[12px] text-paper-ink-3 underline underline-offset-[3px] hover:text-paper-ink">
          {String(url).replace(/^https?:\/\//, '')}
        </a>
      )}

      <p className="m-0 max-w-[46ch] text-[14px] leading-[1.55] text-paper-ink-2">
        {describeMeetingState(state, error)}
      </p>

      <ul className="mt-1 flex w-full max-w-[380px] flex-col gap-[10px] text-left">
        {STEPS.map((step, index) => {
          const status = stepState(step, state, index);
          return (
            <li
              key={step.id}
              data-state={status}
              className={`flex items-center gap-3 text-[13px] ${
                status === 'pending' ? 'text-paper-hint' : 'text-paper-ink'
              } ${status === 'now' ? 'font-medium' : ''}`}
            >
              <span
                className={`w-[10px] h-[10px] shrink-0 rounded-full ${
                  status === 'done' ? 'bg-paper-ink'
                    : status === 'now' ? 'bg-paper-accent animate-pulse'
                      : 'bg-paper-track'
                }`}
              />
              {stepLabel(step, status)}
            </li>
          );
        })}
      </ul>

      {canStop && (
        <button
          type="button"
          onClick={onStop}
          disabled={stopping}
          className="mt-1 rounded-chip bg-paper-bg px-[14px] py-2 text-[13px] font-medium text-paper-ink hover:bg-paper-panel disabled:text-paper-hint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent"
        >
          {stopping ? 'Останавливаем…' : 'Остановить запись'}
        </button>
      )}

      {/* 🔴 Выход после конца встречи. До 23.09.2026 его не было: у готовой или
          упавшей встречи на экране не оставалось ни одной кнопки, и позвать
          бота на следующую встречу было нельзя (обращение d0d83952). */}
      {state === 'PROCESSING' && canStartNew && onNew && (
        <p className="m-0 max-w-[46ch] text-[13px] leading-[1.5] text-paper-ink-3">
          Расшифровка придёт в Историю сама — можно звать бота на следующую встречу.
        </p>
      )}

      {/* «Новая запись» — очистка экрана, а не новая встреча: к лимиту бота её
          не привязываем, иначе при второй встрече в другой вкладке экран
          остался бы без кнопок (ревью 05.10). Лимит честно ответит при /join. */}
      {!canStop && state && onNew && (
        <button
          type="button"
          onClick={onNew}
          className="mt-1 rounded-chip bg-paper-bg px-[14px] py-2 text-[13px] font-medium text-paper-ink hover:bg-paper-panel focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent"
        >
          Новая запись
        </button>
      )}
    </div>
  );
};

export default MeetingState;
